import { forwardRef } from "react";
import flags from "react-phone-number-input/flags";

import { Body } from "capitalrx-components-2.0/lib/typography";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { useId } from "capitalrx-components-2.0/lib/hooks";
import { getHtmlPropsFromBaseProps, getBasePropsFromHtmlProps } from "capitalrx-components-2.0";
import { useInputDisabled, useInputReadonly, useSubmitting, useValidity } from "capitalrx-components-2.0/lib/forms";
import { InputContainer } from "capitalrx-components-2.0/lib/forms/shared";
import { Placeholder } from "capitalrx-components-2.0/lib/forms/Textbox/Placeholder";

import {
  StyledPhoneInputWithCountry,
  StyledPhoneInputWithoutCountry,
  StyledRHFPhoneInputWithCountry,
  StyledRHFPhoneInputWithoutCountry,
} from "./StyledReactPhoneNumberInput";
import { PhoneboxProps } from "./Phonebox";
import styles from "./styles.module.scss";
/**
 * The `Input` component properties.
 */
export interface InputProps extends PhoneboxProps {
  readonly name?: string;
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
  /**
   * Determines whether to display the country code selector and prefix the input value with the code
   */
  readonly useCountryCode?: boolean;
  readonly useReactHookFormVersion?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    label,
    tooltip,
    instructions,
    hideLabel,
    useCountryCode = false,
    required = false,
    onChange,
    useReactHookFormVersion = false,
    autoFocus,
    name,
    placeholder,
    maxLength,
    tabIndex,
    value,
  } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox, "phonebox"],
    testIdPrefix: "phonebox",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const sharedProps = {
    "aria-labelledby": `${id}-label`,
    "aria-invalid": invalid,
    "aria-required": required,
    disabled: disabled,
    autoFocus: autoFocus,
    readOnly: readOnly || submitting,
    maxLength: maxLength,
    tabIndex: disabled || readOnly || submitting ? -1 : tabIndex ?? 0,
    placeholder: placeholder,
    value: value,
    onChange: onChange,
    flags: flags,
  };

  const renderInput = () => {
    if (useReactHookFormVersion) {
      if(useCountryCode) {
        return <StyledRHFPhoneInputWithCountry international countryCallingCodeEditable={false} defaultCountry="US" name={name} {...sharedProps} />;
      } else {
        return <StyledRHFPhoneInputWithoutCountry name={name} {...sharedProps} country="US" />;
      }
    } else {
      if(useCountryCode) {
        // @ts-ignore
        return <StyledPhoneInputWithCountry ref={ref} international countryCallingCodeEditable={false} defaultCountry="US" {...sharedProps} />;
      } else {
        return <StyledPhoneInputWithoutCountry ref={ref} {...sharedProps} country="US" />;
      }
    }
  };

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} hasInstructions={!!instructions} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideLabel={hideLabel}
      >
        <Body className="input-container">{renderInput()}</Body>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
